import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { zodResolver } from '@hookform/resolvers/zod';
import { PropsWithChildren, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { ErrorMessage } from '@hookform/error-message';
import { Loader } from 'src/components/Loader';
import { useToast } from 'src/components/useToast';
import { Material, trpc } from 'src/trpc';
import { cn } from '@/lib/utils';
import {
  materialFormZ,
  MaterialForm as MaterialFormType,
  MaterialStreamTypeEnum,
} from '@libs/schemas';

type FormState = {
  isValid: boolean;
  isDirty: boolean;
};

export interface MaterialFormRef {
  save: () => Promise<void>;
}

interface MaterialFormProps extends PropsWithChildren {
  initialData?: Material;
  onSuccess?: () => void;
  onStateChange?: (state: FormState) => void;
}

export const MaterialForm = forwardRef<MaterialFormRef, MaterialFormProps>(
  ({ initialData, onSuccess, onStateChange }, ref) => {
    const { successToast, errorToast } = useToast();
    const utils = trpc.useUtils();

    // Load container types from tRPC
    const { data: containerTypesResponse, isLoading: loadingContainers } =
      trpc.admin.containerType.getAllContainerTypes.useQuery({
        offset: 0,
        limit: 1000,
        search: '',
        archived: 'active',
      });

    const containerTypes = containerTypesResponse?.containerTypes || [];

    // Real tRPC mutations
    const createMaterial = trpc.admin.material.createMaterial.useMutation({
      onSuccess: () => {
        successToast({
          title: 'Material Created',
          description: 'The material has been successfully created.',
        });
        // Invalidate material queries to refresh the dashboard
        utils.admin.material.getAllMaterials.invalidate();
        utils.admin.material.getAllMaterialsWithContainerInfo.invalidate();
        utils.admin.material.getMaterials.invalidate();
        onSuccess?.();
      },
      onError: (error) => {
        errorToast({
          title: 'Error',
          description: error.message || 'Failed to create material.',
        });
      },
    });

    const updateMaterial = trpc.admin.material.updateMaterial.useMutation({
      onSuccess: () => {
        successToast({
          title: 'Material Updated',
          description: 'The material has been successfully updated.',
        });
        // Invalidate material queries to refresh the dashboard
        utils.admin.material.getAllMaterials.invalidate();
        utils.admin.material.getAllMaterialsWithContainerInfo.invalidate();
        utils.admin.material.getMaterials.invalidate();
        onSuccess?.();
      },
      onError: (error) => {
        errorToast({
          title: 'Error',
          description: error.message || 'Failed to update material.',
        });
      },
    });

    // Transform initialData for the form
    const defaultValues: Partial<MaterialFormType> = initialData
      ? {
          ...initialData,
          extraChargeInCents: initialData.extraChargeInCents ?? 0,
          displayOrder: initialData.displayOrder ?? 1,
          streamType: initialData.streamType ?? MaterialStreamTypeEnum.MIXED,
        }
      : {
          name: '',
          description: '',
          isRestricted: false,
          extraChargeInCents: 0,
          recommendedContainerIds: [],
          preAuthContainerWeightInTons: {},
          archived: false,
          displayOrder: 1,
          streamType: MaterialStreamTypeEnum.MIXED,
          isTemplate: false,
        };

    const form = useForm<MaterialFormType>({
      resolver: zodResolver(materialFormZ),
      defaultValues,
    });

    const { isValid, isDirty, errors } = useFormState({ control: form.control });
    const watchedRecommendedContainers = form.watch('recommendedContainerIds');
    const watchedTonnageConfig = form.watch('preAuthContainerWeightInTons');

    useEffect(() => {
      onStateChange?.({ isValid, isDirty });
    }, [isValid, isDirty, onStateChange]);

    // Handle container selection
    const handleContainerToggle = (containerId: string) => {
      const currentRecommended = form.getValues('recommendedContainerIds');
      const currentTonnage = form.getValues('preAuthContainerWeightInTons');

      if (currentRecommended.includes(containerId)) {
        // Remove container
        const newRecommended = currentRecommended.filter((id) => id !== containerId);
        const newTonnage = { ...currentTonnage };
        delete newTonnage[containerId];

        form.setValue('recommendedContainerIds', newRecommended, { shouldDirty: true });
        form.setValue('preAuthContainerWeightInTons', newTonnage, { shouldDirty: true });
      } else {
        // Add container
        const newRecommended = [...currentRecommended, containerId];
        const newTonnage = { ...currentTonnage, [containerId]: 0 };

        form.setValue('recommendedContainerIds', newRecommended, { shouldDirty: true });
        form.setValue('preAuthContainerWeightInTons', newTonnage, { shouldDirty: true });
      }
    };

    // Handle tonnage change
    const handleTonnageChange = (containerId: string, value: number) => {
      const currentTonnage = form.getValues('preAuthContainerWeightInTons');
      form.setValue(
        'preAuthContainerWeightInTons',
        { ...currentTonnage, [containerId]: value },
        { shouldDirty: true },
      );
    };

    const onSubmit = (values: MaterialFormType) => {
      if (initialData?._id) {
        // For update, include the _id
        updateMaterial.mutate({
          _id: initialData._id,
          name: values.name,
          description: values.description,
          isRestricted: values.isRestricted,
          extraChargeInCents: values.extraChargeInCents,
          recommendedContainerIds: values.recommendedContainerIds,
          preAuthContainerWeightInTons: values.preAuthContainerWeightInTons,
          archived: values.archived,
          displayOrder: values.displayOrder,
          streamType: values.streamType,
        });
      } else {
        // For create, exclude the _id
        createMaterial.mutate({
          name: values.name,
          description: values.description,
          isRestricted: values.isRestricted,
          extraChargeInCents: values.extraChargeInCents,
          recommendedContainerIds: values.recommendedContainerIds,
          preAuthContainerWeightInTons: values.preAuthContainerWeightInTons,
          archived: values.archived,
          displayOrder: values.displayOrder,
          streamType: values.streamType,
        });
      }
    };

    // Expose save function to parent component
    useImperativeHandle(ref, () => ({
      save: async () => {
        const isValid = await form.trigger();
        if (isValid) {
          const values = form.getValues();
          form.reset(values);
          onSubmit(values);
        }
      },
    }));

    if (createMaterial.isPending || updateMaterial.isPending || loadingContainers) {
      return (
        <Loader
          title={
            loadingContainers
              ? 'Loading container types...'
              : initialData?._id
                ? 'Updating material'
                : 'Creating material'
          }
        />
      );
    }

    return (
      <div className="space-y-8">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8" id="material-form">
            {/* Basic Information Section */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
                <p className="text-sm text-muted">Configure the basic details for this material</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-gray-700">
                        Material Name
                      </FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="e.g., Shingles, Concrete, Wood" />
                      </FormControl>
                      <ErrorMessage errors={errors.name} name="name" />
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="displayOrder"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium text-muted">
                        Display Order
                      </FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="1"
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-muted">
                      Description (optional)
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="e.g. Furniture, Clothing, Rugs, Lamps"
                        className="resize-none"
                        rows={3}
                      />
                    </FormControl>
                    <p className="text-xs text-muted">
                      Example materials that fit this category (shown to customers)
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="extraChargeInCents"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium text-muted">
                      Extra Charge (optional)
                    </FormLabel>
                    <FormControl>
                      <NumericFormat
                        value={field.value ? field.value / 100 : ''}
                        onValueChange={(values) => {
                          const numValue = parseFloat(values.value);
                          const cents = isNaN(numValue) ? 0 : Math.round(numValue * 100);
                          field.onChange(cents);
                        }}
                        thousandSeparator={true}
                        prefix={'$'}
                        decimalScale={2}
                        fixedDecimalScale={true}
                        customInput={Input}
                        placeholder="$0.00"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isRestricted"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel
                          className={`text-sm font-medium ${field.value ? 'text-text-primary' : 'text-muted'}`}
                        >
                          Restricted Material
                        </FormLabel>
                        <div className="text-xs text-muted">
                          Requires special handling or approval
                        </div>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          className="data-[state=checked]:bg-primary"
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="archived"
                render={({ field }) => (
                  <FormItem>
                    <div className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel
                          className={`text-sm font-medium ${field.value ? 'text-text-primary' : 'text-muted'}`}
                        >
                          Archive Status
                        </FormLabel>
                        <div className="text-xs text-muted">
                          {field.value
                            ? 'Material is inactive (hidden from customers)'
                            : 'Material is active (visible to customers)'}
                        </div>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={(checked) => field.onChange(checked)}
                          className="data-[state=checked]:bg-primary"
                        />
                      </FormControl>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="streamType"
                render={({ field }) => (
                  <FormItem>
                    <div className="rounded-lg border p-4">
                      <div className="space-y-3">
                        <FormLabel className="text-sm font-medium text-gray-700">
                          Material Stream Type
                        </FormLabel>
                        <p className="text-xs text-muted">
                          Mixed Materials lead to Bundle and Pay-as-you-go pricing. Single Materials
                          lead to Fixed pricing only.
                        </p>
                        <FormControl>
                          <ToggleGroup
                            type="single"
                            value={field.value}
                            onValueChange={(value: string) => {
                              if (value) field.onChange(value as MaterialStreamTypeEnum);
                            }}
                            className="w-full bg-gray-100 rounded-xl p-1.5 gap-1.5"
                          >
                            <ToggleGroupItem
                              value={MaterialStreamTypeEnum.MIXED}
                              className="flex-1 rounded-lg px-6 py-3.5 font-medium text-gray-700 hover:text-gray-900 data-[state=on]:bg-white data-[state=on]:text-primary data-[state=on]:shadow-sm transition-all duration-200"
                            >
                              Mixed Materials
                            </ToggleGroupItem>
                            <ToggleGroupItem
                              value={MaterialStreamTypeEnum.SINGLE}
                              className="flex-1 rounded-lg px-6 py-3.5 font-medium text-gray-700 hover:text-gray-900 data-[state=on]:bg-white data-[state=on]:text-primary data-[state=on]:shadow-sm transition-all duration-200"
                            >
                              Single Material
                            </ToggleGroupItem>
                          </ToggleGroup>
                        </FormControl>
                      </div>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
            </div>

            <Separator />

            {/* Container Configuration Section */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Container Configuration</h3>
                <p className="text-sm text-muted">
                  Select container types and configure minimum tonnage requirements
                </p>
              </div>

              <div>
                <FormLabel className="text-base font-medium">Available Container Types</FormLabel>
                <p className="text-sm text-muted mb-4">
                  Select which container types are recommended for this material
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {containerTypes.map((container) => {
                    const isSelected =
                      watchedRecommendedContainers?.includes(container._id || '') || false;
                    return (
                      <div
                        key={container._id || ''}
                        className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                          isSelected
                            ? 'border-primary/50 hover:border-primary bg-primary/5 '
                            : 'border-slate-200 hover:border-slate-900/50'
                        }`}
                        onClick={() => handleContainerToggle(container._id || '')}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p
                              className={cn('font-medium text-muted', isSelected && 'text-primary')}
                            >
                              {container.name}
                            </p>
                          </div>
                          {isSelected && (
                            <Badge
                              variant="default"
                              className="ml-2 bg-primary/80 hover:bg-primary text-white border-primary/50"
                            >
                              Selected
                            </Badge>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {(watchedRecommendedContainers?.filter((containerId) =>
                containerTypes.some((c) => c._id === containerId),
              ).length || 0) > 0 && (
                <>
                  <Separator />

                  {/* Tonnage Configuration */}
                  <div>
                    <FormLabel className="text-base font-medium">
                      Minimum Tonnage Configuration
                    </FormLabel>
                    <p className="text-sm text-muted mb-4">
                      Set minimum tonnage requirements for each selected container type
                    </p>

                    <div className="space-y-4">
                      {watchedRecommendedContainers
                        ?.filter((containerId) => {
                          // Only show containers that are not archived (exist in active containerTypes)
                          return containerTypes.some((c) => c._id === containerId);
                        })
                        .map((containerId) => {
                          const container = containerTypes.find((c) => c._id === containerId);
                          const currentWeight = watchedTonnageConfig?.[containerId] || 0;
                          const isBestFor =
                            watchedRecommendedContainers?.includes(containerId) || false;

                          return (
                            <div
                              key={containerId}
                              className={`flex items-center gap-4 p-4 border rounded-lg ${
                                isBestFor ? 'border-blue-500 bg-blue-50' : ''
                              }`}
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="font-medium">{container?.name}</p>
                                  {isBestFor && (
                                    <Badge variant="default" className="bg-blue-600 text-xs">
                                      Best For
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <FormLabel className="text-sm">Min. Tonnage:</FormLabel>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={currentWeight}
                                  onChange={(e) =>
                                    handleTonnageChange(
                                      containerId,
                                      parseFloat(e.target.value) || 0,
                                    )
                                  }
                                  className="w-24"
                                />
                                <span className="text-sm text-muted">tons</span>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </>
              )}
            </div>
          </form>
        </Form>
      </div>
    );
  },
);

MaterialForm.displayName = 'MaterialForm';
